// @flow

import { initTelemetry } from '@atlassian/mpac-telemetry';
import {
  createLogBackend,
  createMetalBackend,
  createSentryBackend,
} from '@atlassian/mpac-telemetry/dist/backends';
import { showIEDeprecationFlagIfNeeded } from '@atlassian/mpac-ui';
import { initSentry } from '@atlassian/mpac-utils';
import ReactDOM from 'react-dom';
import { setConfiguration as setReadGridSystemConfig } from 'react-grid-system';

import { getApp } from '.';
import { initMetalMonitoring } from './app/utils/metalMonitoring';
import buildBootstrapData from './buildBootstrapData';
import renderBrowserSupportBanner from './common/components/BrowserSupportBanner';
import registerClicktaleEvents from './common/utils/analytics/clicktale';

import type { BootstrapConfig } from 'amktBootstrap';

const config: BootstrapConfig = window.bootstrap.config;
const amktFrontendVersion = `AMKT_FRONTEND_${process.env.RELEASE_VERSION || 'UNKNOWN'}`;

// Initialize error monitoring using Sentry
initSentry({
  dsn: config.sentry.dsn,
  environment: config.sentry.environment,
  releaseVersion: amktFrontendVersion,
});

setReadGridSystemConfig({ maxScreenClass: 'xl' });

showIEDeprecationFlagIfNeeded();
renderBrowserSupportBanner('browser-support-banner');

// Initialize performance monitoring using Metal
initMetalMonitoring({
  id: config.metal.id,
  env: config.metal.env,
  version: 'AMKT_FRONTEND_STATIC',
});

// Initialize analytics
const initAnalytics = bootstrap => {
  if (!window.ace) return;

  window.ace.analytics.Initializer.initWithPageAnalytics(bootstrap.analytics.aceAnalyticsWriteKey, {
    featureFlags: bootstrap.featureFlags,
    poweredBy: 'AMKT_FRONTEND',
    gasv3Product: 'marketplace',
  });
};

function initAMKTTelemetry(bootstrapConfig: BootstrapConfig) {
  const metalBackend = createMetalBackend({
    env: bootstrapConfig.metal.env,
    id: bootstrapConfig.metal.id,
    version: amktFrontendVersion,
    synthetic: new URLSearchParams(window.location.search).get('source') === 'pollinator',
  });

  const sentryBackend = createSentryBackend();
  const logBackend = createLogBackend({ enabled: false });

  initTelemetry([metalBackend, sentryBackend, logBackend]);
}

const initApp = bootstrap => {
  initAMKTTelemetry(bootstrap);
  initAnalytics(bootstrap);

  const app = getApp(bootstrap);
  const container = document.getElementById('amkt-frontend-content');

  if (container) {
    ReactDOM.render(app, container);
  }

  if (bootstrap.clicktaleId) {
    registerClicktaleEvents(bootstrap.clicktaleId);
  }
};

buildBootstrapData(config)
  .then(initApp)
  .catch(error => console.error('Failed to bootstrap the application', error));
